import type { GetDataRoomAnalyticsResponse } from '@workspace/api-backend'
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import { formatDistanceToNow } from 'date-fns'
import { ChevronDown, ChevronUp, Clock, Eye, Users } from 'lucide-react'
import { useState } from 'react'

const SECTION_COLORS: Record<string, { bg: string; label: string }> = {
  page: { bg: 'bg-zinc-500', label: 'Page' },
  video: { bg: 'bg-blue-500', label: 'Video' },
  pitch_deck: { bg: 'bg-orange-500', label: 'Pitch Deck' },
  document: { bg: 'bg-purple-500', label: 'Document' },
}

function getBlockLabel(
  section: string,
  resourceId: string | null | undefined,
  resourceLabels: Record<string, string>
): string {
  const base = SECTION_COLORS[section]?.label ?? section
  if (section === 'document' && resourceId && resourceLabels[resourceId]) {
    return `${base}: ${resourceLabels[resourceId]}`
  }
  return base
}

function getSectionLabel(section: string): string {
  return SECTION_COLORS[section]?.label ?? section
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`
}

export type AnalyticsData = NonNullable<GetDataRoomAnalyticsResponse['data']>

export function AnalyticsDashboard({ analytics }: { analytics: AnalyticsData }) {
  const { summary, sessions, resourceLabels } = analytics

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard
          icon={<Eye className="size-4" />}
          label="Sessions"
          value={summary.totalViews}
        />
        <SummaryCard
          icon={<Users className="size-4" />}
          label="Unique Viewers"
          value={summary.uniqueViewers}
        />
        <SummaryCard
          icon={<Clock className="size-4" />}
          label="Avg. Time"
          value={formatDuration(summary.avgActiveSeconds)}
        />
      </div>

      {/* Section Breakdown */}
      {Object.keys(summary.sectionBreakdown).length > 0 && (
        <SectionBreakdownBar breakdown={summary.sectionBreakdown} />
      )}

      {/* Session List */}
      {sessions.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-input/50">
          <div className="divide-y divide-border">
            {sessions.map((session) => (
              <SessionRow key={session.id} session={session} resourceLabels={resourceLabels} />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-input/50 py-10 text-muted-foreground">
          <Eye className="size-8 opacity-20" />
          <p className="text-sm opacity-60">No engagement data yet</p>
        </div>
      )}
    </div>
  )
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
}) {
  return (
    <div className="rounded-xl border border-border bg-input/50 p-4">
      <div className="mb-2 flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className="text-xl font-bold text-white">{value}</p>
    </div>
  )
}

function SectionBreakdownBar({ breakdown }: { breakdown: Record<string, number> }) {
  const total = Object.values(breakdown).reduce((sum, v) => sum + v, 0)
  if (total === 0) return null

  return (
    <div className="space-y-3 rounded-xl border border-border bg-input/50 p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">Time Distribution</p>
      <div className="flex h-3 gap-0.5 overflow-hidden rounded-full">
        {Object.entries(breakdown).map(([section, seconds]) => {
          const pct = (seconds / total) * 100
          const config = SECTION_COLORS[section] ?? { bg: 'bg-zinc-600', label: section }
          return (
            <div
              key={section}
              className={cn('rounded-full', config.bg)}
              style={{ width: `${pct}%` }}
              title={`${config.label}: ${formatDuration(seconds)} (${Math.round(pct)}%)`}
            />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(breakdown).map(([section, seconds]) => {
          const config = SECTION_COLORS[section] ?? { bg: 'bg-zinc-600', label: section }
          const pct = Math.round((seconds / total) * 100)
          return (
            <div key={section} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className={cn('size-2 rounded-full', config.bg)} />
              <span>
                {config.label} {pct}%
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SessionRow({
  session,
  resourceLabels,
}: {
  session: AnalyticsData['sessions'][number]
  resourceLabels: Record<string, string>
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-secondary/30"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-elevated">
          {session.viewer.avatarUrl ? (
            <img
              src={session.viewer.avatarUrl}
              alt=""
              className="size-8 rounded-full object-cover"
            />
          ) : (
            <span className="text-xs font-medium text-muted-foreground">
              {(session.viewer.name ?? session.viewer.email).charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{session.viewer.email}</p>
          <p className="truncate text-xs text-muted-foreground">
            {session.viewer.name ? `${session.viewer.name} · ` : ''}
            {formatDistanceToNow(new Date(session.startedAt), { addSuffix: true })} ·{' '}
            {formatDuration(session.totalActiveSeconds)}
          </p>
        </div>
        {/* Inline mini breakdown */}
        <div className="flex h-2 w-24 shrink-0 gap-px overflow-hidden rounded-full">
          {session.timeline.map((block, i) => {
            const totalSeconds = session.totalActiveSeconds || 1
            const pct = (block.durationSeconds / totalSeconds) * 100
            const config = SECTION_COLORS[block.section] ?? {
              bg: 'bg-zinc-600',
              label: block.section,
            }
            return (
              <div
                key={`${block.section}-${i}`}
                className={cn('rounded-full', config.bg)}
                style={{ width: `${pct}%` }}
              />
            )
          })}
        </div>
        {expanded ? (
          <ChevronUp className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      {expanded && session.timeline.length > 0 && (
        <div className="px-5 pb-4">
          <SessionTimeline
            timeline={session.timeline}
            totalSeconds={session.totalActiveSeconds}
            resourceLabels={resourceLabels}
          />
        </div>
      )}
    </div>
  )
}

function SessionTimeline({
  timeline,
  totalSeconds,
  resourceLabels,
}: {
  timeline: AnalyticsData['sessions'][number]['timeline']
  totalSeconds: number
  resourceLabels: Record<string, string>
}) {
  const total = totalSeconds || 1

  return (
    <div className="space-y-2">
      {/* Full timeline bar */}
      <div className="flex h-6 gap-px overflow-hidden rounded-lg">
        {timeline.map((block, i) => {
          const pct = (block.durationSeconds / total) * 100
          const config = SECTION_COLORS[block.section] ?? {
            bg: 'bg-zinc-600',
            label: block.section,
          }
          return (
            <Tooltip key={`${block.section}-${i}`}>
              <TooltipTrigger asChild>
                <div
                  className={cn('flex min-w-0.5 items-center justify-center rounded', config.bg)}
                  style={{ width: `${pct}%` }}
                >
                  {pct > 12 && (
                    <span className="truncate px-1 text-[10px] font-medium text-white/90">
                      {formatDuration(block.durationSeconds)}
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {getBlockLabel(block.section, block.resourceId, resourceLabels)} ·{' '}
                {formatDuration(block.durationSeconds)}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
      {/* Legend — one entry per unique section */}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {[...new Set(timeline.map((b) => b.section))].map((section) => {
          const config = SECTION_COLORS[section] ?? { bg: 'bg-zinc-600', label: section }
          return (
            <span key={section} className="flex items-center gap-1 text-xs text-muted-foreground">
              <span className={cn('size-1.5 rounded-full', config.bg)} />
              {getSectionLabel(section)}
            </span>
          )
        })}
      </div>
    </div>
  )
}
